import { useEffect, useState } from "react";
import axios from "axios";
import { Star, ArrowUpRight, Quote } from "lucide-react";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewsSection() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    axios.get(`${API}/reviews`).then(r => setData(r.data)).catch(() => setData(null));
  }, []);

  // Inject AggregateRating + Review JSON-LD so Google can show stars in search snippets
  useEffect(() => {
    if (!data) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "reviews-jsonld";
    el.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${SITE.url}/#business`,
      name: SITE.name,
      url: SITE.url,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: data.aggregate?.rating ?? 5,
        reviewCount: data.aggregate?.count ?? data.reviews.length,
        bestRating: 5,
        worstRating: 1,
      },
      review: data.reviews.map(r => ({
        "@type": "Review",
        author: { "@type": "Person", name: r.author },
        reviewRating: { "@type": "Rating", ratingValue: r.rating || 5, bestRating: 5 },
        reviewBody: r.text,
      })),
    });
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, [data]);

  if (!data) return null;
  const { aggregate, reviews } = data;
  const isGoogle = aggregate?.source === "google";

  return (
    <section className="py-28 md:py-36 bg-ink-950" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="eyebrow gold-bar">Clients</div>
            <h2 className="font-display text-4xl md:text-6xl mt-4 tracking-tight">
              {isGoogle ? "What clients say on Google" : "What clients say"}
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} size={18} className={`${i < Math.round(aggregate?.rating || 5) ? "fill-gold text-gold" : "text-white/20"}`} />)}
              </div>
              <span className="text-white/65 text-sm">
                <span className="text-white font-medium">{(aggregate?.rating || 5).toFixed(1)}</span>
                {" · "}
                <span>{aggregate?.count || reviews.length} review{aggregate?.count === 1 ? "" : "s"}</span>
              </span>
            </div>
            {isGoogle && (
              <a
                href={aggregate.mapsUri || SITE.gbpUrl}
                target="_blank"
                rel="noreferrer"
                className="text-[12px] uppercase tracking-[0.2em] text-gold hover:text-gold-hi inline-flex items-center gap-1"
                data-testid="see-all-reviews"
              >
                See all on Google <ArrowUpRight size={13}/>
              </a>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reviews.slice(0, 3).map((r, i) => (
            <article
              key={i}
              data-testid={`review-card-${i}`}
              onMouseEnter={() => setActive(i)}
              className={`card-dark p-8 flex flex-col transition-all ${active === i ? "border-gold/40" : ""}`}
            >
              <Quote className="text-gold/60 mb-5" size={22}/>
              <div className="flex gap-0.5 mb-5">
                {[...Array(5)].map((_, j) => <Star key={j} size={14} className={`${j < (r.rating || 5) ? "fill-gold text-gold" : "text-white/15"}`}/>)}
              </div>
              <p className="text-white/85 text-base leading-relaxed font-display italic flex-1">"{r.text}"</p>
              <div className="mt-6 pt-6 border-t border-white/5 flex items-center gap-3">
                {r.avatar && <img src={r.avatar} alt={r.author} className="w-9 h-9 rounded-full object-cover" />}
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-medium truncate">{r.author}</div>
                  <div className="text-white/45 text-xs uppercase tracking-[0.15em] mt-1 truncate">
                    {r.time || r.role || "Verified client"}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 text-center">
          <a
            href={SITE.gbpReviewUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="leave-review-cta"
            className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-white/55 hover:text-gold transition-colors"
          >
            Worked with us? Leave a Google review <ArrowUpRight size={13}/>
          </a>
        </div>
      </div>
    </section>
  );
}
